import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    clampDueDays,
    convertDueValueToUnit,
    formatDueDaysForDisplay,
    getDefaultInvoiceDueDays,
    getDueDaysBoundsForUnit,
    isValidDueDays,
    parseDueDaysInput,
    sanitizeDueValue,
    type TimeUnit,
} from '../utils/invoicing';
import { DUE_DAYS, type DueDaysBounds } from '../constants/invoices/dueDays';

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

interface UseDueDaysFieldResult {
    /** Fallback day count resolved from the organisation's default. */
    defaultDueDays: number;

    /** Current display value of the input, as raw text (may be invalid/unparsed). */
    dueValue: string;

    /** Unit the value is expressed in (e.g. day, week). */
    dueUnitType: TimeUnit;

    /**
     * `dueValue` parsed into canonical days, or `null` if empty/unparseable.
     * Not clamped to range, so only trust it when `dueValueError` is
     * `undefined`.
     */
    parsedDueDays: number | null;

    /** Validation message when `parsedDueDays` is invalid, otherwise undefined. */
    dueValueError: string | undefined;

    /**
     * Call on blur: no-op if the value is already valid; otherwise resets
     * empty input to the organisation's default, or clamps out-of-range
     * input to the nearest bound.
     */
    resolveDueValue: () => void;

    /** Reformats `value` for display in the current unit (not clamped to range). */
    setDueValue: (value: string) => void;

    /** Switches the unit and converts the current value into the new unit. */
    setDueUnitType: (dueUnitType: TimeUnit) => void;

    /** Overwrites the current value and unit based on a raw day count, e.g. after loading saved data or resetting the field. */
    setDueDays: (days?: number) => void;
}

/** Applies initialDefaultDueDays until the field is first set, then ignores it so the chosen unit isn't re-inferred. */
function useDueDaysField(
    initialDefaultDueDays: number | undefined,
    t: TranslateFn,
    bounds: DueDaysBounds = DUE_DAYS.BOUNDS.DEFAULT,
): UseDueDaysFieldResult {
    const defaultDueDays = getDefaultInvoiceDueDays(
        initialDefaultDueDays,
        bounds,
    );

    const initialValue = formatDueDaysForDisplay(
        defaultDueDays,
        undefined,
        bounds,
    );

    const [dueValue, setDueValueState] = useState(initialValue.dueValue);
    const [dueUnitType, setDueUnitTypeState] = useState<TimeUnit>(
        initialValue.unit,
    );
    const hasInteractedRef = useRef(false);
    const dueUnitTypeRef = useRef(dueUnitType);
    dueUnitTypeRef.current = dueUnitType;

    const setDueValue = useCallback((value: string) => {
        hasInteractedRef.current = true;
        const newDueValue = sanitizeDueValue(value, dueUnitTypeRef.current);
        setDueValueState(newDueValue);
    }, []);

    const setDueUnitType = useCallback(
        (newUnitType: TimeUnit) => {
            if (newUnitType === dueUnitType) {
                return;
            }

            hasInteractedRef.current = true;

            const newDueValue = convertDueValueToUnit(
                dueValue,
                dueUnitType,
                newUnitType,
                bounds,
            );

            setDueUnitTypeState(newUnitType);
            setDueValueState(newDueValue);
        },
        [dueUnitType, dueValue, bounds],
    );

    const parsedDueDays = useMemo(
        () => parseDueDaysInput(dueValue, dueUnitType),
        [dueValue, dueUnitType],
    );

    const dueValueError = useMemo(() => {
        if (isValidDueDays(parsedDueDays, bounds)) {
            return undefined;
        }

        const dueDaysBounds = getDueDaysBoundsForUnit(dueUnitType, bounds);
        return t('Settings:invoices_admin:default_due_days_validation_error', {
            min: dueDaysBounds.min,
            max: dueDaysBounds.max,
            unit: t(`Invoice:time_options:${dueUnitType}`),
        });
    }, [parsedDueDays, dueUnitType, t, bounds]);

    // Stabilised against unit changes: setDueUnitType calls this via the
    // effect below keyed only on the invoice id, and the identity must stay
    // constant across a days<->weeks toggle so that effect's deps don't
    // re-fire and revert an in-progress edit.
    const setDueDays = useCallback(
        (days?: number) => {
            const preferredUnit = hasInteractedRef.current
                ? dueUnitTypeRef.current
                : undefined;
            const { dueValue, unit } = formatDueDaysForDisplay(
                days,
                preferredUnit,
                bounds,
            );
            setDueValueState(dueValue);
            setDueUnitTypeState(unit);
            hasInteractedRef.current = true;
        },
        [bounds],
    );

    const resolveDueValue = useCallback(() => {
        if (!dueValueError) {
            return;
        }
        setDueDays(clampDueDays(parsedDueDays ?? defaultDueDays, bounds));
    }, [parsedDueDays, dueValueError, defaultDueDays, setDueDays, bounds]);

    useEffect(() => {
        if (hasInteractedRef.current) {
            return;
        }
        const { dueValue, unit } = formatDueDaysForDisplay(
            initialDefaultDueDays,
            undefined,
            bounds,
        );
        setDueValueState(dueValue);
        setDueUnitTypeState(unit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDefaultDueDays, bounds]);

    return {
        defaultDueDays,
        dueValue,
        dueUnitType,
        parsedDueDays,
        dueValueError,
        resolveDueValue,
        setDueValue,
        setDueUnitType,
        setDueDays,
    };
}

export default useDueDaysField;
export type { UseDueDaysFieldResult };
